import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { Map } from 'immutable';
import moment from 'moment';

import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import variables from 'src/style/variable/variables.module.scss';
import useMediaQuery from '@mui/material/useMediaQuery';
import _ from 'lodash';
import DeviceRotate from 'src/component/UI/DeviceRotate';

import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from 'src/component/UI/Button';

import CheckIcon from '@mui/icons-material/CheckSharp';
import EditIcon from '@mui/icons-material/EditSharp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleSharp';
import ConfirmModal from 'src/component/UI/ConfirmModal';
import EditWorkTicketModal from 'src/component/UI/EditWorkTicketModal';
import DataTable from 'src/component/UI/DataTable';
import { updateWorkTicket, addCircuitsToWorkTicket } from 'src/module/workTicket/action';
import { getDevicesSelectedFilters, getSelectedUnit } from 'src/module/filter/selector';
import { getPluralWorkTicketLabel } from 'src/module/authentication/selector';

import PermissionFence from '../PermissionFence';
import DeviceLinkCombo from 'src/component/UI/DeviceLinkCombo';
import { useTranslation } from 'src/i18n';
import { formatCircuitTag } from 'src/utils/utils';

type SelectedCircuit = {
  id: string;
  tag: string;
  label: string;
};

export default function WorkTicketsList (props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);

  const [filter, setFilter] = useState(null);
  const [sort, setSort] = useState(null);
  const pluralWorkTicketLabel = useSelector(getPluralWorkTicketLabel);

  const {
    deviceId,
    showSearchBar,
    suppressHeader,
    hideDeviceTag,
    showCompletedAt,
    showDeleteIcon,
    showIncludeCompletedOption,
    selectors,
    fetch,
    tableId
  } = props;
  const [pageSize, setPageSize] = useState(10);
  const workTickets: any = useSelector(selectors.getWorkTickets);
  const loading: any = useSelector(selectors.getLoadingWorkTickets);
  const numPages: any = useSelector(selectors.getNumWorkTicketsPages);
  const totalWorkTickets: any = useSelector(selectors.getTotalWorkTickets);
  const selectedUnit: any = useSelector(getSelectedUnit);

  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [deleteWorkTicketModalOpen, setDeleteWorkTicketModalOpen] = useState(false);
  const [workTicketUUID, setWorkTicketUUID] = useState('');
  const [workTicketId, setWorkTicketId] = useState('');

  // State for editing work tickets
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedWorkTicket, setSelectedWorkTicket] = useState<any>(null);

  // State for expanding circuits list
  const [rowToShowAllCircuits, setRowToShowAllCircuits] = useState<string | null>(null);

  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const [showRotateMessage, setShowRotateMessage] = useState(isMobile && isPortrait);
  const headerFilters = useSelector(getDevicesSelectedFilters);
  useEffect(() => {
    setShowRotateMessage(isMobile && isPortrait);
  }, [isMobile, isPortrait]);

  const handleUpdateFilter = () => {
    dispatch(fetch(!includeCompleted, selectedUnit, page, pageSize, filter, sort, deviceId));
    setIncludeCompleted(!includeCompleted);
  };

  // Open edit modal for a work ticket
  const openEditModal = (workTicket: any) => {
    setSelectedWorkTicket(workTicket);
    setEditModalOpen(true);
  };

  // Memoize circuit processing per work ticket to avoid recalculation
  // Store full device objects for circuits, not just metadata
  const circuitsCache = useMemo(() => {
    const cache: Record<string, any[]> = {};
    workTickets.forEach((workTicket: any) => {
      if (!workTicket) return;

      const workTicketId = workTicket.get('id');
      const devices = workTicket.get('devices');
      if (!devices) {
        cache[workTicketId] = [];
        return;
      }

      // Handle both Immutable and plain arrays
      let devicesList: any[];
      if (devices.toJS) {
        devicesList = devices.toJS();
      } else if (Array.isArray(devices)) {
        devicesList = devices;
      } else {
        cache[workTicketId] = [];
        return;
      }

      if (!Array.isArray(devicesList)) {
        cache[workTicketId] = [];
        return;
      }

      cache[workTicketId] = devicesList;
    });
    return cache;
  }, [workTickets]);

  const getCircuitsForWorkTicket = (workTicket: any): any[] => {
    if (!workTicket) {
      return [];
    }
    const workTicketId = workTicket.get('id');
    return circuitsCache[workTicketId] || [];
  };

  // i.e. UUID is work ticket id in db and workTicketStringId = '9326781' (shown on work ticket list)
  const assignWorkTicket = (uuid, workTicketStringId) => {
    setWorkTicketUUID(uuid);
    setWorkTicketId(workTicketStringId);
  };

  const toggleDeleteWorkTicketModal = () => {
    setDeleteWorkTicketModalOpen(!deleteWorkTicketModalOpen);
  };

  const handleComplete = (uuid, workTicketStringId) => {
    toggleDeleteWorkTicketModal();
    assignWorkTicket(uuid, workTicketStringId);
  };

  // Memoize columns so they update when rowToShowAllCircuits changes
  const columns: any = useMemo(() => {
    // Helper function to render circuits column (modern, styled version) - defined inside useMemo to capture current state
    const circuitsCol = (circuits: any[], workTicketId: string | number) => {
      if (!circuits || circuits.length === 0) {
        return null;
      }

      const allCircuits = circuits;
      // Convert to string for consistent comparison
      const workTicketIdStr = String(workTicketId);
      const isExpanded = workTicketIdStr === rowToShowAllCircuits;
      const showExpandable = allCircuits.length > 10;

      if (showExpandable && !isExpanded) {
        const first10 = allCircuits.slice(0, 10);
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {first10.map((circuit, index) => (
              <Box key={index} sx={{ whiteSpace: 'nowrap' }}>
                <DeviceLinkCombo device={Map(circuit)} />
              </Box>
            ))}
            <Box
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setRowToShowAllCircuits(workTicketIdStr);
              }}
              sx={{
                marginTop: '4px',
                display: 'inline-block',
              }}
            >
              <Chip
                label={`+${allCircuits.length - 10} more`}
                size="small"
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  fontSize: '11px',
                  height: '24px',
                  borderColor: variables.primary,
                  color: variables.primary,
                  '&:hover': {
                    backgroundColor: 'rgba(74, 65, 197, 0.1)',
                    borderColor: variables.primary,
                  },
                  transition: 'all 0.2s ease',
                }}
              />
            </Box>
          </Box>
        );
      }

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {allCircuits.map((circuit, index) => (
            <Box key={index} sx={{ whiteSpace: 'nowrap' }}>
              <DeviceLinkCombo device={Map(circuit)} />
            </Box>
          ))}
          {showExpandable && isExpanded && (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setRowToShowAllCircuits(null);
              }}
              sx={{
                marginTop: '4px',
                display: 'inline-block',
              }}
            >
              <Chip
                label="Show less"
                size="small"
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  fontSize: '11px',
                  height: '24px',
                  borderColor: variables.primary,
                  color: variables.primary,
                  '&:hover': {
                    backgroundColor: 'rgba(74, 65, 197, 0.1)',
                    borderColor: variables.primary,
                  },
                  transition: 'all 0.2s ease',
                }}
              />
            </Box>
          )}
        </Box>
      );
    };
    const cols: any = [
      {
        id: 'sap_notifications.sap_notification_id',
        Header: t('workTickets.id'),
        accessor: (row: any) => row.get('sap_notification_id'),
        Cell: (row: any) => (
          <span
            className='clickable-cell'
            onClick={() => openEditModal(row.cell.row.original)}
            style={{ cursor: 'pointer', color: variables.primary, fontWeight: 500 }}
          >
            {row.cell.value || 'N/A'}
          </span>
        )
      }
    ];

    if (!hideDeviceTag) {
      cols.push({
        id: 'device_tag',
        Header: t('alarms.deviceTag'),
        accessor: (row: any) => row.get('device_tag'),
        Cell: (row: any) => {
          const workTicket = row.cell.row.original;
          const workTicketId = workTicket.get('id');
          const circuits = getCircuitsForWorkTicket(workTicket);
          // If there are circuits, show them all in a list
          if (circuits && circuits.length > 0) {
            return (
              <Box key={`circuits-${workTicketId}-${rowToShowAllCircuits || 'none'}`}>
                {circuitsCol(circuits, String(workTicketId))}
              </Box>
            );
          }

          // If no circuits, show the main device
          return (
            <DeviceLinkCombo device={workTicket} />
          );
        },
        minWidth: '250px',
        maxWidth: '450px',
      });
    }

    cols.push(
      {
        id: 'sap_notifications.description',
        Header: t('workTickets.description'),
        accessor: (row: any) => row.get('description'),
        maxWidth: '250px',
        Cell: (row: any) => (
          <span style={{ color: row.cell.value ? '#333' : '#999' }}>
            {row.cell.value || '—'}
          </span>
        )
      },
      {
        id: 'sap_notifications.revision',
        Header: t('workTickets.revision'),
        accessor: (row: any) => row.get('revision'),
        Cell: (row: any) => (
          <span style={{ color: row.cell.value ? '#333' : '#999' }}>
            {row.cell.value || '—'}
          </span>
        )
      },
      {
        id: 'sap_notifications.note',
        Header: t('workTickets.note'),
        accessor: (row: any) => row.get('note'),
        maxWidth: '250px',
        Cell: (row: any) => (
          <span style={{ color: row.cell.value ? '#333' : '#999' }}>
            {row.cell.value || '—'}
          </span>
        )
      },
      {
        id: 'sap_notifications.created_at',
        Header: t('notesList.createdAt'),
        accessor: (row: any) => row.get('notification_created_at'),
        Cell: (row: any) => (row.cell.value ? moment(row.cell.value).format('MMM D, YYYY') : '—')
      }
    );

    if (showCompletedAt) {
      cols.push({
        id: 'sap_notifications.completed_at',
        Header: t('workTickets.completedAt'),
        accessor: (row: any) => row.get('completed_at'),
        Cell: (row: any) => (row.cell.value ? moment(row.cell.value).format('MMM D, YYYY') : '—')
      });
    }

    // Status/Actions column
    if (showDeleteIcon) {
      cols.push({
        id: 'actions',
        Header: '',
        width: '120px',
        disableSortBy: true,
        Cell: (row: any) => {
          const workTicket = row.cell.row.original;
          const isCompleted = workTicket?.get('completed_at') != null;

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
              {isCompleted ? (
                <Tooltip title={t('workTickets.completed')}>
                  <CheckIcon style={{ fontSize: 18, color: '#4caf50' }} />
                </Tooltip>
              ) : (
                <>
                  <PermissionFence can='edit-sap-notification'>
                    <Tooltip title={t('workTickets.complete')}>
                      <Button
                        onClick={() => handleComplete(workTicket.get('id'), workTicket.get('sap_notification_id'))}
                        icon={<CheckCircleOutlineIcon style={{ fontSize: 16 }} />}
                        compact
                      />
                    </Tooltip>
                  </PermissionFence>
                  <PermissionFence can='edit-sap-notification'>
                    <Tooltip title={t('workTickets.edit')}>
                      <Button
                        onClick={() => openEditModal(workTicket)}
                        icon={<EditIcon style={{ fontSize: 16 }} />}
                        compact
                      />
                    </Tooltip>
                  </PermissionFence>
                </>
              )}
            </div>
          );
        }
      });
    }

    return cols;
  }, [rowToShowAllCircuits, hideDeviceTag, showCompletedAt, showDeleteIcon, workTickets, t]);


  const fetchPage = (_page, _pageSize, _filter, _sort) => {
    setPage(_page);
    setPageSize(_pageSize);
    setSort(_sort);
    setFilter(_filter);
  };
  const ref_filters = useRef(null);
  const ref_others = useRef(null);
  useEffect(() => {
    const others = `${page} ${pageSize} ${filter} ${sort} ${deviceId} ${includeCompleted}`;
    if (!_.isEqual(ref_filters.current, headerFilters.toJS()) || others !== ref_others.current) {
      ref_filters.current = headerFilters.toJS();
      ref_others.current = others;


      dispatch(fetch(includeCompleted, headerFilters, page, pageSize, filter, sort, deviceId));
    }
  }, [dispatch, headerFilters, page, pageSize, filter, sort, deviceId, includeCompleted]);

  const workTicketFetchObj = { includeCompleted, selectedUnit, page, pageSize, filter, sort, deviceId };

  const deleteWorkTicketModalSubmit = () => {
    dispatch(updateWorkTicket(workTicketUUID, Map({ 'completed_at': new Date() }), workTicketFetchObj));
    toggleDeleteWorkTicketModal();
  };

  // Edit modal handlers
  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedWorkTicket(null);
  };

  const handleEditSubmit = (updatedData: any, circuits: SelectedCircuit[] | null) => {
    if (selectedWorkTicket) {
      const workTicketId = selectedWorkTicket.get('id');

      // Update work ticket fields (description, revision, note)
      dispatch(updateWorkTicket(workTicketId, Map(updatedData), workTicketFetchObj));

      // Update circuits if changed
      if (circuits !== null) {
        const circuitIds = circuits.map(c => c.id);
        dispatch(addCircuitsToWorkTicket(workTicketId, circuitIds, null));
      }
    }
  };

  const handleCompleteFromModal = (uuid: string, workTicketStringId: string) => {
    handleComplete(uuid, workTicketStringId);
  };

  const deleteMessage = t('workTickets.closeConfirm', { id: workTicketId });
  return (
    <div data-testid='note-list' className='alarms-list'>
      <ConfirmModal
        title={t('modal.areYouSure')}
        message={deleteMessage}
        open={deleteWorkTicketModalOpen}
        handleClose={() => toggleDeleteWorkTicketModal()}
        handleReject={() => toggleDeleteWorkTicketModal()}
        handleConfirm={deleteWorkTicketModalSubmit}
      />
      <EditWorkTicketModal
        open={editModalOpen}
        handleClose={closeEditModal}
        handleSubmit={handleEditSubmit}
        handleComplete={handleCompleteFromModal}
        workTicket={selectedWorkTicket}
        existingCircuits={selectedWorkTicket ? getCircuitsForWorkTicket(selectedWorkTicket).map(c => ({
          id: c.id,
          tag: c.tag,
          label: c.parent_tag ? formatCircuitTag(c.tag, c.parent_tag) : c.tag
        })) : []}
      />
      <PermissionFence can='edit-eht-note'></PermissionFence>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {!suppressHeader && (<h2>{props.title}</h2>)}
          {showRotateMessage ? <DeviceRotate /> :
            <DataTable
              key={`workTicketsTable-${rowToShowAllCircuits || 'none'}`}
              pagination
              filterable={showSearchBar}
              filterLabel={t('workTickets.search', { label: pluralWorkTicketLabel || t('workTickets.title') })}
              columns={columns}
              data={workTickets}
              numPages={numPages}
              totalRecords={totalWorkTickets}
              loading={loading}
              fetchPage={fetchPage}
              tableId={tableId}
            />
          }
          {showIncludeCompletedOption && !showRotateMessage ?
            (<FormControlLabel
              control={
                <Checkbox
                  checked={includeCompleted}
                  onChange={handleUpdateFilter}
                  name='includeCompleted'
                  color='primary'
                />
              }
              label={t('workTickets.showCompleted', { label: pluralWorkTicketLabel || t('workTickets.title') })}
            />) : null
          }
        </Grid>
      </Grid>
    </div>
  );
}

WorkTicketsList.propTypes = {
  title: PropTypes.string,
  deviceId: PropTypes.string,
  hideDeviceTag: PropTypes.bool,
  showSearchBar: PropTypes.bool,
  suppressHeader: PropTypes.bool,
  pagination: PropTypes.bool,
  showIncludeCompletedOption: PropTypes.bool,
  showCompletedAt: PropTypes.bool,
  showDeleteIcon: PropTypes.bool,
  selectors: PropTypes.object.isRequired,
  fetch: PropTypes.func.isRequired,
  tableId: PropTypes.string
};
WorkTicketsList.defaultProps = {
  title: null,
  deviceId: null,
  hideDeviceTag: false,
  showSearchBar: false,
  suppressHeader: false,
  showCompletedAt: false,
  showDeleteIcon: false,
  pagination: true,
  showIncludeCompletedOption: false,
  tableId: 'workTicketsTable'
};
